// src/components/comunicacion/NoticiasInternas.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";
import { useLogos } from "../../../../context/LogoProvider";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";
import ConfirmDialog from "../../../Lms/Components/Shared/ConfirmDialog";
import sigmaDelete from '../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../Pages/img/Editar.png';
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";


// ?? Configuración dinámica de URLs basada en API_BASE
const SERVER_URL = API_BASE.replace(/\/api\/?$/, ''); // Elimina /api al final
const UPLOAD_ENDPOINT = `${API_BASE}/upload/noticia`;

const getTodayISOForNews = () => {
  return new Date().toISOString().slice(0, 10);
};

const NoticiasInternas = ({ onNavigate, initialItem }) => {
  const { getSystemConfig } = useLogos();
  const ESTADOS = [
    { value: "publicada", label: "Publicada" },
    { value: "borrador", label: "Borrador" },
    { value: "archivada", label: "Archivada" },
  ];

  const [noticias, setNoticias] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [areasNegocio, setAreasNegocio] = useState([]);
  const [categoriasDB, setCategoriasDB] = useState([]);

  const [filtros, setFiltros] = useState({
    texto: "",
    area: "Todos",
    categoria: "Todas",
    estado: "todos", // todos | publicada | borrador | archivada
    urgencia: "todos",
  });

  const [formData, setFormData] = useState(() => ({
    titulo: "",
    resumen: "",
    contenido: "",
    fechaPublicacion: getTodayISOForNews(),
    area: "Recursos Humanos",
    categoria: "Comunicados generales",
    estado: "borrador",
    destacada: false,
    tipoNoticia: "Baja",
    imagen: null,
  }));

  const [errores, setErrores] = useState({});
  const [imagenNombre, setImagenNombre] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previousImagePath, setPreviousImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagenPreview, setImagenPreview] = useState(null);

  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    onClose: () => { }
  });

  const showAlertDialog = (title, message, variant = 'info') => {
    setAlertDialog({
      open: true,
      title,
      message,
      variant,
      onClose: () => setAlertDialog(prev => ({ ...prev, open: false }))
    });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'danger',
    onConfirm: () => { },
    onClose: () => { }
  });

  const showConfirmDialog = (title, message, onConfirm, variant = 'danger') => {
    setConfirmDialog({
      open: true,
      title,
      message,
      variant,
      onConfirm,
      onClose: () => setConfirmDialog(prev => ({ ...prev, open: false }))
    });
  };

  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  const reloadNoticias = async () => {
    try {
      setLoading(true);
      const res = await axios.get(ENDPOINTS.noticias);
      const rawData = res.data;

      const catMap = {};
      categoriasDB.forEach(c => catMap[c.idCategoria ?? c.IdCategoria] = c.nombre ?? c.Nombre);
      const areaMap = {};
      areasNegocio.forEach(a => areaMap[a.idAreaNegocio ?? a.IdAreaNegocio] = a.nombre ?? a.Nombre);

      const hydratedData = rawData.map(n => ({
        ...n,
        id: n.idNoticia ?? n.IdNoticia,
        fechaPublicacion: (n.fechaPublicacion ?? n.FechaPublicacion) ? (n.fechaPublicacion ?? n.FechaPublicacion).split('T')[0] : '',
        titulo: n.titulo ?? n.Titulo,
        resumen: n.resumen ?? n.Resumen,
        contenido: n.contenido ?? n.Contenido,
        imagen: n.imagenUrl ?? n.ImagenUrl,
        categoria: catMap[n.idCategoria ?? n.IdCategoria] || 'General',
        area: areaMap[n.idAreaNegocio ?? n.IdAreaNegocio] || 'Corporativo',
        estado: (n.idEstado ?? n.IdEstado) === 1 ? 'publicada' : ((n.idEstado ?? n.IdEstado) === 2 ? 'borrador' : 'archivada'),
        destacada: n.destacada ?? n.Destacada ?? false,
        tipoNoticia: n.tipoNoticia ?? n.TipoNoticia ?? 'Baja',
      }));

      // Ordenar: Destacadas primero, luego por fecha de publicación descendente
      hydratedData.sort((a, b) => {
        if (a.destacada && !b.destacada) return -1;
        if (!a.destacada && b.destacada) return 1;
        const dateA = new Date(a.fechaPublicacion || 0).getTime();
        const dateB = new Date(b.fechaPublicacion || 0).getTime();
        if (dateB !== dateA) return dateB - dateA;
        return b.id - a.id; // Tie-breaker final e inamovible
      });

      setNoticias(hydratedData);
    } catch (e) {
      console.error("Error reloading noticias:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [resAreas, resCats, resNoticias] = await Promise.all([
          axios.get(ENDPOINTS.areasNegocios).catch(e => { console.error("Err areas", e); return { data: [] }; }),
          axios.get(ENDPOINTS.categorias).catch(e => { console.error("Err cats", e); return { data: [] }; }),
          axios.get(ENDPOINTS.noticias)
        ]);

        setAreasNegocio(resAreas.data);
        setCategoriasDB(resCats.data);

        const catMap = {};
        resCats.data.forEach(c => catMap[c.idCategoria ?? c.IdCategoria] = c.nombre ?? c.Nombre);
        const areaMap = {};
        resAreas.data.forEach(a => areaMap[a.idAreaNegocio ?? a.IdAreaNegocio] = a.nombre ?? a.Nombre);

        const rawData = resNoticias.data;
        const hydratedData = rawData.map(n => ({
          ...n,
          id: n.idNoticia ?? n.IdNoticia,
          fechaPublicacion: (n.fechaPublicacion ?? n.FechaPublicacion) ? (n.fechaPublicacion ?? n.FechaPublicacion).split('T')[0] : '',
          titulo: n.titulo ?? n.Titulo,
          resumen: n.resumen ?? n.Resumen,
          contenido: n.contenido ?? n.Contenido,
          imagen: n.imagenUrl ?? n.ImagenUrl,
          categoria: catMap[n.idCategoria ?? n.IdCategoria] || 'General',
          area: areaMap[n.idAreaNegocio ?? n.IdAreaNegocio] || 'Corporativo',
          estado: (n.idEstado ?? n.IdEstado) === 1 ? 'publicada' : ((n.idEstado ?? n.IdEstado) === 2 ? 'borrador' : 'archivada'),
          destacada: n.destacada ?? n.Destacada ?? false,
          tipoNoticia: n.tipoNoticia ?? n.TipoNoticia ?? 'Baja',
        }));

        // Ordenar: Destacadas primero, luego por fecha de publicación descendente
        hydratedData.sort((a, b) => {
          if (a.destacada && !b.destacada) return -1;
          if (!a.destacada && b.destacada) return 1;
          const dateA = new Date(a.fechaPublicacion || 0).getTime();
          const dateB = new Date(b.fechaPublicacion || 0).getTime();
          if (dateB !== dateA) return dateB - dateA;
          return b.id - a.id; // Tie-breaker final e inamovible
        });

        setNoticias(hydratedData);

        let noticiaToSelect = null;
        if (initialItem) {
          noticiaToSelect = hydratedData.find(n => n.id === initialItem.id);
        }
        if (!noticiaToSelect && hydratedData.length > 0) {
          noticiaToSelect = hydratedData[0];
        }

        if (noticiaToSelect) {
          setSelectedId(noticiaToSelect.id);
          setFormData({
            titulo: noticiaToSelect.titulo,
            resumen: noticiaToSelect.resumen,
            contenido: noticiaToSelect.contenido,
            fechaPublicacion: noticiaToSelect.fechaPublicacion,
            area: noticiaToSelect.area,
            categoria: noticiaToSelect.categoria,
            estado: noticiaToSelect.estado,
            destacada: noticiaToSelect.destacada,
            tipoNoticia: noticiaToSelect.tipoNoticia || "Baja",
            imagen: noticiaToSelect.imagen || null,
            imagenPreview: null,
          });
          setImagenNombre(noticiaToSelect.imagen ? noticiaToSelect.imagen.split('/').pop() : null);
          setPreviousImagePath(noticiaToSelect.imagen || null);
        }

      } catch (e) {
        console.error("Error loading initial data:", e);
        setError("No se pudieron cargar las noticias. Por favor, intenta nuevamente.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [initialItem]);

  const handleFiltrosChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrores((prev) => ({ ...prev, imagen: 'Solo se permiten archivos de imagen.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrores((prev) => ({ ...prev, imagen: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);
    const blobUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, imagenPreview: blobUrl }));
    setImagenNombre(file.name);
    setErrores((prev) => ({ ...prev, imagen: '' }));
  };

  const limpiarFormulario = () => {
    setSelectedId(null);
    setFormData({
      titulo: "",
      resumen: "",
      contenido: "",
      fechaPublicacion: getTodayISOForNews(),
      area: areasNegocio.length > 0 ? (areasNegocio[0].nombre ?? areasNegocio[0].Nombre) : "Recursos Humanos",
      categoria: categoriasDB.length > 0 ? (categoriasDB[0].nombre ?? categoriasDB[0].Nombre) : "Comunicados generales",
      estado: "borrador",
      destacada: false,
      tipoNoticia: "Baja",
      imagen: null,
    });
    setErrores({});
    setImagenNombre(null);
    setImagenPreview(null);
    setPreviousImagePath(null);
    setSelectedFile(null);
  };

  const executeDelete = async () => {
    const noticiaToDelete = deleteDialog.item;
    if (!noticiaToDelete) return;

    try {
      if (noticiaToDelete.imagen) {
        try {
          await axios.delete(UPLOAD_ENDPOINT, {
            params: { filePath: noticiaToDelete.imagen },
          });
        } catch (imgErr) {
          console.warn('Err deleting physical image:', imgErr);
        }
      }
      await axios.delete(`${ENDPOINTS.noticias}/${noticiaToDelete.id}`);
      await reloadNoticias();
      if (selectedId === noticiaToDelete.id) {
        limpiarFormulario();
      }
      showAlertDialog("Eliminada", "La noticia ha sido eliminada.", "success");
      setDeleteDialog({ open: false, item: null });
    } catch (e) {
      console.error("Error al eliminar noticia:", e);
      showAlertDialog("Error", "No se pudo eliminar la noticia.", "error");
    }
  };

  const handleEliminar = (noticia) => {
    setDeleteDialog({ open: true, item: noticia });
  };

  const validarFormulario = () => {
    const nuevoErrores = {};
    if (!formData.titulo.trim()) nuevoErrores.titulo = "El título es obligatorio.";
    if (!formData.resumen.trim()) nuevoErrores.resumen = "El resumen es obligatorio.";
    if (!formData.contenido.trim()) nuevoErrores.contenido = "El contenido es obligatorio.";
    if (!formData.fechaPublicacion) nuevoErrores.fechaPublicacion = "La fecha de publicación es obligatoria.";
    // if (!formData.imagen && !selectedFile) nuevoErrores.imagen = "La imagen de portada es obligatoria.";
    setErrores(nuevoErrores);
    return Object.keys(nuevoErrores).length === 0;
  };

  const handleGuardar = async (e) => {
    if (e) e.preventDefault();
    if (!validarFormulario()) return;

    try {
      setUploading(true);
      let finalImageUrl = formData.imagen;

      if (selectedFile) {
        if (selectedId && previousImagePath) {
          try {
            await axios.delete(UPLOAD_ENDPOINT, { params: { filePath: previousImagePath } });
          } catch (delErr) { console.warn(delErr); }
        }

        const dataUpl = new FormData();
        dataUpl.append('file', selectedFile);
        const resUpl = await axios.post(UPLOAD_ENDPOINT, dataUpl, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        finalImageUrl = resUpl.data.filePath;
        setPreviousImagePath(finalImageUrl);
      }

      const idUsuario = getSessionUserId();
      const noticiaData = {
        titulo: formData.titulo,
        resumen: formData.resumen,
        contenido: formData.contenido,
        fechaPublicacion: formData.fechaPublicacion,
        destacada: formData.destacada,
        imagenUrl: finalImageUrl || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === formData.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === formData.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === formData.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === formData.categoria)?.IdCategoria) || 1,
        idEstado: formData.estado === "publicada" ? 1 : (formData.estado === "borrador" ? 2 : 3),
        idUsuarioCreador: idUsuario,
        tipoNoticia: formData.tipoNoticia || "Baja",
        activo: formData.estado !== "archivada",
      };

      if (selectedId) {
        noticiaData.idNoticia = selectedId;
        await axios.put(ENDPOINTS.noticias, noticiaData);
        showAlertDialog("Éxito", "Noticia actualizada con éxito.", "success");
      } else {
        const response = await axios.post(ENDPOINTS.noticias, noticiaData);
        setSelectedId(response.data);
        showAlertDialog("Éxito", "Noticia creada con éxito.", "success");
      }

      setShowModal(false);
      await reloadNoticias();

    } catch (e) {
      console.error('Error al guardar:', e);
      showAlertDialog("Error", "Error al guardar la noticia.", "error");
    } finally {
      setUploading(false);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');

  const handleNuevaNoticia = () => {
    limpiarFormulario();
    setModalMode('create');
    setShowModal(true);
  };

  const handleSeleccionarNoticia = (noticia) => {
    setSelectedId(noticia.id);
    setFormData({
      titulo: noticia.titulo,
      resumen: noticia.resumen,
      contenido: noticia.contenido,
      fechaPublicacion: noticia.fechaPublicacion,
      area: noticia.area,
      categoria: noticia.categoria,
      estado: noticia.estado,
      destacada: noticia.destacada,
      tipoNoticia: noticia.tipoNoticia || "Baja",
      imagen: noticia.imagen || null,
      imagenPreview: null,
    });
    setErrores({});
    setImagenNombre(noticia.imagen ? noticia.imagen.split('/').pop() : null);
    setPreviousImagePath(noticia.imagen || null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handlePublicar = async () => {
    if (!validarFormulario()) return;

    try {
      setUploading(true);
      let finalImageUrl = formData.imagen;

      if (selectedFile) {
        if (previousImagePath) {
          try {
            await axios.delete(UPLOAD_ENDPOINT, { params: { filePath: previousImagePath } });
          } catch (delErr) { console.warn(delErr); }
        }
        const dataUpl = new FormData();
        dataUpl.append('file', selectedFile);
        const resUpl = await axios.post(UPLOAD_ENDPOINT, dataUpl, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        finalImageUrl = resUpl.data.filePath;
      }

      const idUsuario = getSessionUserId();
      const noticiaData = {
        idNoticia: selectedId || 0,
        titulo: formData.titulo || "",
        resumen: formData.resumen || "",
        contenido: formData.contenido || "",
        fechaPublicacion: formData.fechaPublicacion || getTodayISOForNews(),
        destacada: formData.destacada || false,
        imagenUrl: finalImageUrl || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === formData.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === formData.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === formData.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === formData.categoria)?.IdCategoria) || 1,
        idEstado: 1,
        idUsuarioCreador: idUsuario || 0,
        tipoNoticia: formData.tipoNoticia || "Baja",
        activo: true
      };

      if (selectedId) {
        await axios.put(ENDPOINTS.noticias, noticiaData);
      } else {
        await axios.post(ENDPOINTS.noticias, noticiaData);
      }

      await reloadNoticias();
      setShowModal(false);
      showAlertDialog("Publicada", "Noticia publicada correctamente.", "success");
    } catch (error) {
      console.error('Error al publicar:', error);
      showAlertDialog("Error", "Error al publicar la noticia.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleArchivar = async (id) => {
    try {
      const noticiaActual = noticias.find(n => n.id === id);
      if (!noticiaActual) return;

      const noticiaData = {
        idNoticia: id || 0,
        titulo: noticiaActual.titulo || "",
        resumen: noticiaActual.resumen || "",
        contenido: noticiaActual.contenido || "",
        fechaPublicacion: noticiaActual.fechaPublicacion || getTodayISOForNews(),
        destacada: noticiaActual.destacada || false,
        imagenUrl: noticiaActual.imagen || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.IdCategoria) || 1,
        idEstado: 3,
        idUsuarioCreador: getSessionUserId() || 0,
        tipoNoticia: noticiaActual.tipoNoticia || "Baja",
        activo: false
      };

      await axios.put(ENDPOINTS.noticias, noticiaData);
      await reloadNoticias();
      showAlertDialog("Archivada", "Noticia archivada correctamente.", "success");
      setShowModal(false);
    } catch (error) {
      console.error('Error al archivar:', error);
      showAlertDialog("Error", "Error al archivar la noticia.", "error");
    }
  };

  const handleDesarchivar = async (id) => {
    try {
      const noticiaActual = noticias.find(n => n.id === id);
      if (!noticiaActual) return;

      const noticiaData = {
        idNoticia: id || 0,
        titulo: noticiaActual.titulo || "",
        resumen: noticiaActual.resumen || "",
        contenido: noticiaActual.contenido || "",
        fechaPublicacion: noticiaActual.fechaPublicacion || getTodayISOForNews(),
        destacada: noticiaActual.destacada || false,
        imagenUrl: noticiaActual.imagen || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.IdCategoria) || 1,
        idEstado: 1,
        idUsuarioCreador: getSessionUserId() || 0,
        tipoNoticia: noticiaActual.tipoNoticia || "Baja",
        activo: true
      };

      await axios.put(ENDPOINTS.noticias, noticiaData);
      await reloadNoticias();
      showAlertDialog("Desarchivada", "Noticia desarchivada y publicada correctamente.", "success");
      setShowModal(false);
    } catch (error) {
      console.error('Error al desarchivar:', error);
      showAlertDialog("Error", "Error al desarchivar la noticia.", "error");
    }
  };

  const handlePasarBorrador = async (id) => {
    try {
      const noticiaActual = noticias.find(n => n.id === id);
      if (!noticiaActual) return;

      const noticiaData = {
        idNoticia: id || 0,
        titulo: noticiaActual.titulo || "",
        resumen: noticiaActual.resumen || "",
        contenido: noticiaActual.contenido || "",
        fechaPublicacion: noticiaActual.fechaPublicacion || getTodayISOForNews(),
        destacada: noticiaActual.destacada || false,
        imagenUrl: noticiaActual.imagen || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.IdCategoria) || 1,
        idEstado: 2,
        idUsuarioCreador: getSessionUserId() || 0,
        tipoNoticia: noticiaActual.tipoNoticia || "Baja",
        activo: true
      };

      await axios.put(ENDPOINTS.noticias, noticiaData);
      await reloadNoticias();
      showAlertDialog("Borrador", "Noticia regresada a borrador. Ahora puede editarla.", "info");
      setShowModal(false);
    } catch (error) {
      console.error('Error al pasar a borrador:', error);
      showAlertDialog("Error", "Error al cambiar estado.", "error");
    }
  };

  const handleToggleDestacada = async (id) => {
    try {
      const noticiaActual = noticias.find(n => n.id === id);
      if (!noticiaActual) return;

      const nuevoEstado = !noticiaActual.destacada;

      const noticiaData = {
        idNoticia: id || 0,
        titulo: noticiaActual.titulo || "",
        resumen: noticiaActual.resumen || "",
        contenido: noticiaActual.contenido || "",
        fechaPublicacion: noticiaActual.fechaPublicacion || getTodayISOForNews(),
        destacada: nuevoEstado || false,
        imagenUrl: noticiaActual.imagen || "",
        idAreaNegocio: (areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.idAreaNegocio ?? areasNegocio.find(a => (a.nombre ?? a.Nombre) === noticiaActual.area)?.IdAreaNegocio) || 1,
        idCategoria: (categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.idCategoria ?? categoriasDB.find(c => (c.nombre ?? c.Nombre) === noticiaActual.categoria)?.IdCategoria) || 1,
        idEstado: noticiaActual.idEstado || 1,
        idUsuarioCreador: getSessionUserId() || 0,
        tipoNoticia: noticiaActual.tipoNoticia || "Baja",
        activo: noticiaActual.idEstado !== 3
      };

      await axios.put(ENDPOINTS.noticias, noticiaData);
      await reloadNoticias();
    } catch (error) {
      console.error('Error al cambiar destacado:', error);
      showAlertDialog("Error", "No se pudo actualizar el estado destacado.", "error");
    }
  };

  const noticiasFiltradas = useMemo(() => {
    return noticias.filter((n) => {
      if (filtros.texto && !((n.titulo || "").toLowerCase().includes(filtros.texto.toLowerCase()) || (n.resumen || "").toLowerCase().includes(filtros.texto.toLowerCase()))) return false;
      if (filtros.area !== "Todos" && n.area !== filtros.area) return false;
      if (filtros.categoria !== "Todas" && n.categoria !== filtros.categoria) return false;
      if (filtros.estado !== "todos" && n.estado !== filtros.estado) return false;
      if (filtros.urgencia !== "todos" && n.tipoNoticia !== filtros.urgencia) return false;
      return true;
    });
  }, [noticias, filtros]);

  const getEtiquetaEstado = (estado) => {
    const found = ESTADOS.find((e) => e.value === estado);
    return found ? found.label : estado;
  };

  const isEditable = modalMode === 'create' || formData.estado === 'borrador';

  return (
    <div className="intra-news-page">
      <header className="intra-news-header">
        <div>
          {onNavigate && (
            <button
              type="button"
              className="intra-button-ghost btn-cancel"
              onClick={() => onNavigate("dashboard")}
              style={{
                marginBottom: '0.75rem'
              }}
            >
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>â†</span>
              <span>Volver al dashboard</span>
            </button>
          )}
          <h1 className="intra-news-title">Noticias internas</h1>
        </div>
      </header>

      <section className="intra-news-toolbar">
        <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
          <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
            <label className="intra-label">Buscar</label>
            <input name="texto" type="text" className="intra-input" value={filtros.texto} onChange={handleFiltrosChange} placeholder="Título o resumen..." />
          </div>
          <div className="intra-filter-group" style={{ width: '180px' }}>
            <label className="intra-label">Área</label>
            <select name="area" className="intra-select" value={filtros.area} onChange={handleFiltrosChange}>
              <option key="all-areas" value="Todos">Todos</option>
              {areasNegocio.map((a, idx) => <option key={a.idAreaNegocio || a.IdAreaNegocio || idx} value={a.nombre || a.Nombre}>{a.nombre || a.Nombre}</option>)}
            </select>
          </div>
          <div className="intra-filter-group" style={{ width: '180px' }}>
            <label className="intra-label">Categoría</label>
            <select name="categoria" className="intra-select" value={filtros.categoria} onChange={handleFiltrosChange}>
              <option key="all-cats" value="Todas">Todas</option>
              {categoriasDB.map((c, idx) => <option key={c.idCategoria || c.IdCategoria || idx} value={c.nombre || c.Nombre}>{c.nombre || c.Nombre}</option>)}
            </select>
          </div>
          <div className="intra-filter-group" style={{ width: '150px' }}>
            <label className="intra-label">Estado</label>
            <select name="estado" className="intra-select" value={filtros.estado} onChange={handleFiltrosChange}>
              <option key="all-status" value="todos">Todos</option>
              {ESTADOS.map((e, idx) => <option key={e.value || idx} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div className="intra-filter-group" style={{ width: '150px' }}>
            <label className="intra-label">Prioridad</label>
            <select name="urgencia" className="intra-select" value={filtros.urgencia} onChange={handleFiltrosChange}>
              <option value="todos">Todas</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </select>
          </div>
          <button type="button" className="btn-action" onClick={handleNuevaNoticia} style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}>
            + Nueva noticia
          </button>
        </div>
      </section>

      <section className="intra-news-layout" style={{ display: 'block' }}>
        <div className="intra-news-list-panel" style={{ width: '100%' }}>
          <div className="intra-panel-header">
            <h2 className="intra-panel-title">Listado de noticias</h2>
            <p className="intra-news-count">{noticiasFiltradas.length} noticia{noticiasFiltradas.length === 1 ? "" : "s"} encontradas</p>
          </div>

          {noticiasFiltradas.length === 0 ? (
            <p className="intra-avisos-empty">No se encontraron noticias.</p>
          ) : (
            <div className="intra-avisos-table-wrapper">
              <table className="intra-avisos-table" style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '60px', textAlign: 'center' }}>DEST.</th>
                    <th>IMAGEN</th>
                    <th>TÍTULO</th>
                    <th>PRIORIDAD</th>
                    <th>Área de Negocio</th>
                    <th>Categoría</th>
                    <th>FECHA</th>
                    <th>ESTADO</th>
                    <th>EDITAR</th>
                    <th>ELIMINAR</th>
                  </tr>
                </thead>
                <tbody>
                  {noticiasFiltradas.map((noticia) => {
                    const bS = noticia.destacada ? '2px solid #fbbf24' : '2px solid transparent';
                    const tdMid = { borderTop: bS, borderBottom: bS };
                    return (
                      <tr key={noticia.id} style={{ backgroundColor: noticia.destacada ? '#fffbeb' : 'transparent' }}>
                        <td style={{ textAlign: 'center', ...tdMid, borderLeft: bS, borderTopLeftRadius: noticia.destacada ? '12px' : '0', borderBottomLeftRadius: noticia.destacada ? '12px' : '0' }}>
                          <span
                            onClick={() => handleToggleDestacada(noticia.id)}
                            style={{
                              cursor: 'pointer',
                              color: noticia.destacada ? '#fbbf24' : '#e2e8f0',
                              fontSize: '1.75rem',
                              lineHeight: 1,
                              display: 'inline-block',
                              transition: 'color 0.2s ease, transform 0.15s ease',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#fbbf24'}
                            onMouseLeave={e => e.currentTarget.style.color = noticia.destacada ? '#fbbf24' : '#e2e8f0'}
                            title={noticia.destacada ? 'Quitar destacada' : 'Marcar como destacada'}
                          >★</span>
                        </td>
                        <td style={{ padding: '0.5rem', ...tdMid }}>
                          {noticia.imagen ? (
                            <img
                              src={noticia.imagen.startsWith('http') ? noticia.imagen : `${SERVER_URL}${noticia.imagen}`}
                              alt=""
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }}
                            />
                          ) : '📄'}
                        </td>
                        <td style={tdMid}>
                          <div className="intra-avisos-title-main">{noticia.titulo}</div>
                          {noticia.destacada && <span style={{ fontSize: '0.7rem', backgroundColor: '#fbbf24', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>Destacada</span>}
                        </td>
                        <td style={tdMid}>
                          <span style={{
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            backgroundColor: noticia.tipoNoticia === 'Alta' ? '#FEE2E2' : noticia.tipoNoticia === 'Media' ? '#FEF3C7' : '#E0F2FE',
                            color: noticia.tipoNoticia === 'Alta' ? '#B91C1C' : noticia.tipoNoticia === 'Media' ? '#B45309' : '#0369A1'
                          }}>
                            {noticia.tipoNoticia || 'Baja'}
                          </span>
                        </td>
                        <td style={tdMid}>
                          <span className="intra-avisos-meta">{noticia.area}</span>
                        </td>
                        <td style={tdMid}>
                          <span className="intra-avisos-meta">{noticia.categoria}</span>
                        </td>
                        <td style={tdMid}>{noticia.fechaPublicacion}</td>
                        <td style={tdMid}>
                          <span className={"intra-news-status-pill intra-news-status-pill--" + noticia.estado}>
                            {getEtiquetaEstado(noticia.estado)}
                          </span>
                        </td>
                        <td style={tdMid}>
                          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleSeleccionarNoticia(noticia)}>
                            <img src={sigmaEdit} alt="Editar" style={{ width: '40px' }} />
                          </button>
                        </td>
                        <td style={{ ...tdMid, borderRight: bS, borderTopRightRadius: noticia.destacada ? '12px' : '0', borderBottomRightRadius: noticia.destacada ? '12px' : '0' }}>
                          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleEliminar(noticia)}>
                            <img src={sigmaDelete} alt="Eliminar" style={{ width: '40px' }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '2rem' }}>
          <div style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {modalMode === 'edit' && (
                  <button type="button" onClick={() => handleToggleDestacada(selectedId)} style={{ background: 'none', border: 'none', fontSize: '1.75rem', cursor: 'pointer', color: formData.destacada ? '#fbbf24' : '#9ca3af' }}>★</button>
                )}
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{modalMode === 'create' ? "Nueva noticia" : "Detalle"}</h2>
              </div>
              <button type="button" onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '2rem', color: '#64748b', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ padding: '2rem', overflowY: 'auto' }}>
              {isEditable ? (
                <form onSubmit={handleGuardar}>
                  <div className="intra-form-group">
                    <label className="intra-label">Título</label>
                    <input name="titulo" type="text" className="intra-input" value={formData.titulo} onChange={handleFormChange} placeholder="Título..." />
                  </div>
                  <div className="intra-form-group">
                    <label className="intra-label">Resumen</label>
                    <textarea name="resumen" className="intra-textarea" rows={3} value={formData.resumen} onChange={handleFormChange} />
                  </div>
                  <div className="intra-form-group">
                    <label className="intra-label">Contenido</label>
                    <textarea name="contenido" className="intra-textarea" rows={7} value={formData.contenido} onChange={handleFormChange} />
                  </div>

                  <div className="intra-form-group">
                    <label className="intra-label">Imagen de portada</label>
                    <div className={`file-uploader${(formData.imagen || selectedFile) ? ' file-uploader--filled' : ''}`}>
                      {(formData.imagen || selectedFile) && (
                        <div className="file-uploader__badge">
                          <span>✓</span>
                          <span>Archivo cargado</span>
                        </div>
                      )}
                      {(formData.imagen || selectedFile) && (
                        <div className="file-uploader__preview">
                          <img
                            src={formData.imagenPreview || (formData.imagen?.startsWith('http') ? formData.imagen : `${SERVER_URL}${formData.imagen}`)}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                          />
                        </div>
                      )}
                      <label className="file-uploader__label">
                        <input type="file" accept="image/*" onChange={handleFileChange} onClick={(e) => { e.target.value = ''; }} />
                        <span className="file-uploader__icon">{(formData.imagen || selectedFile) ? 'â†»' : 'â˜'}</span>
                        <span className="file-uploader__text">{(formData.imagen || selectedFile) ? 'Cambiar imagen' : 'Seleccionar imagen'}</span>
                        <span className="file-uploader__hint">JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</span>
                      </label>
                    </div>
                  </div>

                  <div className="intra-form-row" style={{ display: 'flex', gap: '1rem' }}>
                    <div className="intra-form-group" style={{ flex: 1 }}>
                      <label className="intra-label">Prioridad / Urgencia</label>
                      <select name="tipoNoticia" className="intra-select" value={formData.tipoNoticia} onChange={handleFormChange}>
                        <option value="Baja">Baja</option>
                        <option value="Media">Media</option>
                        <option value="Alta">Alta</option>
                      </select>
                    </div>
                  </div>

                  <div className="intra-form-row" style={{ display: 'flex', gap: '1rem' }}>
                    <div className="intra-form-group" style={{ flex: 1 }}>
                      <label className="intra-label">Área</label>
                      <select name="area" className="intra-select" value={formData.area} onChange={handleFormChange}>
                        {areasNegocio.map((a, idx) => <option key={a.idAreaNegocio || a.IdAreaNegocio || idx} value={a.nombre || a.Nombre}>{a.nombre || a.Nombre}</option>)}
                      </select>
                    </div>
                    <div className="intra-form-group" style={{ flex: 1 }}>
                      <label className="intra-label">Categoría</label>
                      <select name="categoria" className="intra-select" value={formData.categoria} onChange={handleFormChange}>
                        {categoriasDB.map((c, idx) => <option key={c.idCategoria || c.IdCategoria || idx} value={c.nombre || c.Nombre}>{c.nombre || c.Nombre}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
                    <button
                      type="submit"
                      className="btn-action"
                      disabled={uploading}
                      style={{ opacity: uploading ? 0.7 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}
                    >
                      {uploading ? 'Guardando...' : '💾 Guardar Borrador'}
                    </button>
                    <button
                      type="button"
                      className="btn-action"
                      onClick={handlePublicar}
                      disabled={uploading}
                      style={{ opacity: uploading ? 0.7 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}
                    >
                      {uploading ? 'Publicando...' : '📢 Publicar'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  {formData.imagen && (
                    <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                      <img src={formData.imagen.startsWith('http') ? formData.imagen : `${SERVER_URL}${formData.imagen}`} alt="" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }} />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <span style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>{formData.area}</span>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      textTransform: 'uppercase',
                      backgroundColor: formData.tipoNoticia === 'Alta' ? '#FEE2E2' : formData.tipoNoticia === 'Media' ? '#FEF3C7' : '#E0F2FE',
                      color: formData.tipoNoticia === 'Alta' ? '#B91C1C' : formData.tipoNoticia === 'Media' ? '#B45309' : '#0369A1'
                    }}>
                      {formData.tipoNoticia}
                    </span>
                    <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{formData.fechaPublicacion}</span>
                  </div>
                  <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{formData.titulo}</h3>
                  <p style={{ fontStyle: 'italic', color: '#4b5563', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{formData.resumen}</p>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{formData.contenido}</div>

                  <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: '1px solid #eee', paddingTop: '1.5rem' }}>
                    {formData.estado === 'publicada' && (
                      <>
                        <button type="button" className="intra-button-primary" onClick={() => handlePasarBorrador(selectedId)} style={{ backgroundColor: '#3b82f6' }}>✏️  Habilitar Edición</button>
                        <button type="button" className="intra-button-primary" onClick={() => handleArchivar(selectedId)}>📦 Archivar</button>
                      </>
                    )}
                    {formData.estado === 'archivada' && (
                      <button type="button" className="intra-button-primary" onClick={() => handleDesarchivar(selectedId)} style={{ backgroundColor: '#C92439' }}>📂 Desarchivar</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={alertDialog.open} onClose={alertDialog.onClose} title={alertDialog.title} message={alertDialog.message} variant={alertDialog.variant} />
      <ConfirmDialog open={confirmDialog.open} onClose={confirmDialog.onClose} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} />
      <ConfirmDeleteDialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })} onConfirm={executeDelete} roleName={deleteDialog.item?.titulo || "noticia"} />
    </div>
  );
};

export default NoticiasInternas;

